import React, { useState, useContext, useEffect } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Text, toast } from "@foris/avocado-suite";
import { useLocation, useParams } from "react-router-dom";
import { Section, Session } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { AppContext } from "../../context/EditSessionsContext";
import { Types as TypeSwitch } from "../../context/switchPage.reducer";
import { Types, FormActions, EditedSession } from "../../context/formData.reducer";
import { Types as TypeLink } from "../../context/linkData.reducer";
import { IEvent, IWeek } from "../../../ISections";
import { compare } from "../../utils/compareEdited";
import { applyValidationResponse } from "../../utils/applyValidationResponse";
import { buildAssignationChangesets } from "../../utils/assignationChangesets";
import { buildProposalInput, canSaveDraft, outcomeErrorKey } from "@utils/assignmentProposal";
import { useSubmitAssignmentProposal } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { PreflightIntent } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { setActiveProposal } from "@context/useAssignmentProposalStore";
import { useActiveProposal } from "../../hooks/useActiveProposal";
import { useIsEditorLocked } from "../../hooks/useIsEditorLocked";
import { useFinishProposalEdition } from "../../hooks/useFinishProposalEdition";
import { useSyncProposalUrl } from "../../hooks/useSyncProposalUrl";
import { ContextEDH } from "@context/ContextEDH";
import { usePastSessionsVisibilityStore } from "../../context/usePastSessionsVisibilityStore";
import useSessionsCrudMutation from "../../hooks/useSessionsCrudMutation";
import { useLoadBlockingThreshold } from "../../hooks/useLoadBlockingThreshold";
import Modal from "@components/modal";
import AccordionSections from "../../components/Accordion/AccordionSections";
import EditSession from "./EditSession";
import CreateSession from "./CreateSession";
import AssignationActionsHeader from "../../components/assignation-header";
import css from "./formEdit.module.scss";

interface IFormEdit {
  heightHeader: number;
  viewSwitch?: React.ReactNode;
}

interface ILocationState {
  currentSelectedWeek?: IWeek;
  sectionId?: string;
  sessionId?: string;
  event?: IEvent;
  createSession?: boolean;
}

const FormEdit: React.FC<IFormEdit> = (props: IFormEdit) => {
  const { heightHeader, viewSwitch } = props;
  const location = useLocation();
  const locationState: ILocationState = location.state;
  const currentSelectedWeek = locationState?.currentSelectedWeek;
  const [resultSessionsCrud, submitSessionsCrud] = useSessionsCrudMutation({
    dryRun: true,
    skipValidations: true,
  });
  const { scenario, origin }: IParams = useParams();
  const { state, dispatch } = useContext(AppContext);
  const { state: stateEDH } = useContext(ContextEDH);
  const { submit: submitProposal } = useSubmitAssignmentProposal();
  const resetPastSessionsVisibility = usePastSessionsVisibilityStore(s => s.reset);
  const { loadBlockingThreshold } = useLoadBlockingThreshold();

  useEffect(() => {
    loadBlockingThreshold();

    return () => {
      resetPastSessionsVisibility();
    };
  }, []);
  const [warning, setWarning] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  // What the "sessions without assignation" warning was opened for, so confirming it runs the
  // matching terminal action (the dry-run validation, or saving the draft).
  const [warningIntent, setWarningIntent] = useState<PreflightIntent>("validate");
  // A draft the backend rejected with validation errors; the effect below applies it exactly like
  // a dry-run response, so both render through the same code and the same render-scope state.
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const isEditorLocked = useIsEditorLocked();
  const activeProposal = useActiveProposal();
  const syncProposalUrl = useSyncProposalUrl();
  const finishProposalEdition = useFinishProposalEdition();
  const [, setVariables] = useState<FormActions>(null);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const handleCompareBySection = (
    sessions: Session[],
    section: Section,
    checked: boolean,
    newSessions: EditedSession[],
  ) => {
    const mockSessions = newSessions.map(session => ({
      id: session?.id,
      origin: null,
      vacancies: null,
      events: null,
      ...(session ?? {}),
      section,
    }));

    const cloneSessions = [...mockSessions, ...sessions];
    const payload = {
      checked: !checked,
      sessionsOfSection: cloneSessions,
      sectionId: section.id,
    };

    const allSessionsMatch = cloneSessions.every(session => compare(session, state));

    if (allSessionsMatch) {
      dispatch({ type: Types.SelectedSessionsBySection, payload });
      setWarning(false);
      return !checked;
    } else {
      setWarning(true);
      setVariables({ type: Types.SelectedSessionsBySection, payload });
    }

    return checked;
  };

  const handleCompareSession = (
    session: Session,
    editedSession: EditedSession = null,
    section: Section = null,
  ) => {
    const isClonedSession = (session: EditedSession) => !!session?.isCloned;
    const { selectedCreateSession, editedSessions } = state?.form;

    // Revisa si la sesión a crear tuvo alguna edición y todavía no ha sido
    // guardada (CASO EDITAR)
    if (!selectedCreateSession && session) {
      dispatch({ type: Types.SelectedSessions, payload: session });
      setWarning(false);
    }

    // Si se está seleccionando una nueva sesión ya creada
    if (!selectedCreateSession && editedSession) {
      if (isClonedSession(editedSession)) {
        dispatch({ type: Types.SelectedSessions, payload: editedSession });
        setWarning(false);
      } else {
        const mockSession: Session = {
          id: editedSession?.id,
          origin: null,
          vacancies: null,
          events: null,
          section,
        };

        if (compare(mockSession, state)) {
          dispatch({ type: Types.SelectedSessions, payload: mockSession });
          setWarning(false);
        }
      }
    }

    // Revisa si la nueva sesión tuvo alguna edición (CASO NUEVA SESIÓN)
    if (selectedCreateSession && session) {
      const blocks = !!editedSessions?.blocks?.selected;
      const classrooms = !!editedSessions?.classrooms?.length;
      const instructors = !!editedSessions?.instructors?.length;
      const intervals = !!editedSessions?.intervals?.some(interval => interval?.checked);
      if (blocks || classrooms || instructors || intervals) {
        setWarning(true);
        setVariables({ type: Types.SelectedSessions, payload: session });
      } else {
        dispatch({ type: Types.CleanFormCreateSession });
        dispatch({ type: Types.SelectedSessions, payload: session });
      }
    }
    if (selectedCreateSession && editedSession) {
      const mockSession: Session = {
        id: editedSession?.id,
        origin: null,
        vacancies: null,
        events: null,
        section,
      };
      if (compare(mockSession, state)) {
        dispatch({ type: Types.SelectedSessions, payload: mockSession });
        setWarning(false);
      }
    }
  };

  useEffect(() => {
    if (!!state?.form?.editedSessions?.blocks && locationState) {
      if (currentSelectedWeek && !state.link.selectedWeek) {
        dispatch({ type: TypeLink.SetSelectedWeek, payload: currentSelectedWeek });
      }

      if (!!locationState?.sessionId && !!state?.link?.sections?.length) {
        const { sessionId } = locationState;
        let selectedSession: EditedSession = {};

        state?.link?.sections.forEach((section: Section) => {
          const sessions = section.sessions || [];
          const unasignedSessions = section.unasignedSessions || [];
          const allSessions = [...sessions, ...unasignedSessions];

          allSessions.forEach(session => {
            if (
              !Object.keys(selectedSession).length &&
              !!session?.id &&
              String(session?.id) === String(sessionId)
            ) {
              selectedSession = session;
            }
          });
        });

        if (Object.keys(selectedSession).length && !state?.form?.sessionFromLocationSelected) {
          dispatch({ type: Types.SelectedSessions, payload: selectedSession });
          dispatch({ type: Types.SetSessionFromLocationSelected, payload: true });
        }
      }

      if (
        locationState?.event &&
        locationState?.event?.resource?.blockRange &&
        !!stateEDH?.base?.base?.user?.abilities?.can_assign_blocks
      ) {
        window.dispatchEvent(new Event("enable-edition-blocks"));
        dispatch({
          type: Types.SetIsEditionEnabled,
          payload: {
            day: true,
          },
        });
        dispatch({
          type: Types.SetIsEditionEnabled,
          payload: {
            blocks: true,
          },
        });

        dispatch({
          type: Types.BlocksEditedSessions,
          payload: {
            blocks: {
              ...state?.form?.editedSessions?.blocks,
              startTime: locationState?.event?.resource?.blockRange?.start?.startingTime,
              endTime: locationState?.event?.resource?.blockRange?.end?.endingTime,
              day: locationState?.event?.resource?.blockRange?.start?.day,
              selected: "hours",
            },
          },
        });
      }

      if (locationState?.sectionId && locationState?.createSession) {
        const section = state?.link?.sections?.find(
          (section: Section) => section.id === locationState.sectionId,
        );

        !!section && dispatch({ type: Types.SelectedCreateSession, payload: section });
      }

      location.state = {};
      window.history.replaceState({}, "");
    }
  }, [state?.form?.editedSessions?.blocks]);

  useEffect(() => {
    applyValidationResponse({ responseData: resultSessionsCrud?.data, state, dispatch });

    if (resultSessionsCrud?.error) setError(true);
  }, [resultSessionsCrud]);

  /**
   * Saves the current changes as a proposal draft and stays bound to it, so saving again updates
   * it. Failures never use the error modal of this page: it reloads the page and loses the edits.
   */
  const saveDraft = async () => {
    if (isSavingDraft || isEditorLocked || !canSaveDraft(activeProposal)) return;

    setIsSavingDraft(true);
    dispatch({ type: TypeSwitch.SetLoading, payload: true });

    const outcome = await submitProposal({
      scenarioId: scenario,
      originId: origin,
      input: buildProposalInput({
        linkId: state?.link?.info?.id,
        changesets: buildAssignationChangesets(state?.form),
        draft: true,
        proposalId: activeProposal?.proposalId,
      }),
    });

    setIsSavingDraft(false);
    dispatch({ type: TypeSwitch.SetLoading, payload: false });

    if (!outcome) return;

    if (outcome.kind === "sent") {
      // The answered id is the one to keep (a new proposal gets its id on the first save).
      setActiveProposal({
        linkId: String(state?.link?.info?.id),
        proposalId: outcome.proposalId,
        mode: "draft",
        origin: activeProposal?.origin ?? "editor",
        rejectionReason: null,
        reviewer: activeProposal?.reviewer ?? null,
      });
      syncProposalUrl(outcome.proposalId);
      setIsDraftSaved(true);
      return;
    }

    if (outcome.kind === "validation") {
      // A result left from an earlier validation must not keep this one from showing.
      applyValidationResponse({
        responseData: outcome.validation,
        state: {
          ...state,
          result: { ...state?.result, resultValidation: null, createValidation: null },
        },
        dispatch,
      });
      return;
    }

    toast.error(t(outcomeErrorKey(outcome, "draft")), { autoClose: 5000 });
  };

  return (
    <>
      <AssignationActionsHeader
        viewSwitch={viewSwitch}
        locationCurrentSelectedWeek={currentSelectedWeek}
        isSavingDraft={isSavingDraft}
        onSubmitValidations={() => {
          dispatch({ type: TypeSwitch.SetLoading, payload: true });
          submitSessionsCrud();
        }}
        onSaveDraft={saveDraft}
        onShowValidationWarnings={(showWarnings, intent = "validate") => {
          setWarningIntent(intent);
          setShowValidationWarning(showWarnings);
        }}
      />

      <section className={cx(css.content, css[`content__height${heightHeader}`])}>
        <aside className={cx(css.aside)}>
          <AccordionSections
            onCompareBySession={handleCompareSession}
            onCompareBySection={handleCompareBySection}
          />
        </aside>

        {state?.form?.selectedCreateSession && (
          <CreateSession warning={warning} setWarning={setWarning} />
        )}
        {!state?.form?.selectedCreateSession && <EditSession />}
      </section>

      <Modal
        typeState="warning"
        title={t("assignation-edit.form.notifications.sessions-without-assignation.title")}
        show={showValidationWarning}
        textButtonPrincipal={t(
          "assignation-edit.form.notifications.sessions-without-assignation.confirm",
        )}
        textButtonSecondary={t(
          "assignation-edit.form.notifications.sessions-without-assignation.cancel",
        )}
        onClickSecondary={() => setShowValidationWarning(false)}
        onClickPrincipal={() => {
          setShowValidationWarning(false);
          if (warningIntent === "draft") {
            saveDraft();
          } else {
            dispatch({ type: TypeSwitch.SetLoading, payload: true });
            submitSessionsCrud();
          }
          dispatch({ type: Types.CleanUnsavableSessionsToCreate });
        }}
      >
        <Text>
          {t("assignation-edit.form.notifications.sessions-without-assignation.description")}
        </Text>
      </Modal>

      <Modal
        typeState="confirm"
        title={t("assignation-edit.proposal.finish-modal.draft-title")}
        show={isDraftSaved}
        textButtonPrincipal={t("assignation-edit.proposal.finish-modal.finish")}
        onClickPrincipal={finishProposalEdition}
        textButtonSecondary={t("assignation-edit.proposal.finish-modal.continue-editing")}
        onClickSecondary={() => setIsDraftSaved(false)}
      >
        <Text>{t("assignation-edit.proposal.finish-modal.draft-description")}</Text>
      </Modal>

      <Modal
        typeState="error"
        title={t("assignation-edit.form.notifications.generic-validation-error.title")}
        show={error}
        textButtonPrincipal={t(
          "assignation-edit.form.notifications.generic-validation-error.action",
        )}
        onClickPrincipal={() => window.location.reload()}
      >
        <Text>{t("assignation-edit.form.notifications.generic-validation-error.description")}</Text>
      </Modal>
    </>
  );
};

export default FormEdit;
